import { faChartColumn, faClose, faFileCsv, faFilePdf } from "@fortawesome/free-solid-svg-icons"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { useTimeClock } from "../../../hooks/TimeClock/useTimeClock"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { getTimeClockEntryCard } from "../../../Objects/TimeClock/TimeClockCards"
import type { TimeClockLaborCost } from "../../../Objects/TimeClock/TimeClockLaborCost"
import type { TimeClockEmployeeSummary } from "../../../Objects/TimeClock/TimeClockEmployeeSummary"
import type { TimeClockEntry } from "../../../Objects/TimeClock/TimeClockEntry"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { getDateKeyInTimeZone } from "../../../utils/timeZone"
import GetSpace from "../../../utils/space"
import { TimeClockLaborCostPopup } from "./TimeClockLaborCostPopup"
import { TimeClockReportsEntriesPopup } from "./TimeClockReportsEntriesPopup"

interface TimeClockReportsPopupProps {
  employees: TimeClockEmployeeSummary[]
  onClose: () => void
}

export const TimeClockReportsPopup: React.FC<TimeClockReportsPopupProps> = ({ employees, onClose }) => {
  const { t, i18n } = useTranslation(["timeClock", "helpPopup"])

  const { isLoading, downloadReportPdf, downloadReportCsv, getLaborCost, getEntries } = useTimeClock()

  const [laborCost, setLaborCost] = useState<TimeClockLaborCost[]>([])

  const [entries, setEntries] = useState<TimeClockEntry[]>([])

  const [employeeId, setEmployeeId] = useState(employees[0]?.employeeId ?? "")

  const [startDate, setStartDate] = useState(
    () => `${getDateKeyInTimeZone(new Date(), companyMemory.timeZoneId).slice(0, 8)}01`,
  )

  const [endDate, setEndDate] = useState(() => getDateKeyInTimeZone(new Date(), companyMemory.timeZoneId))

  const [isEntriesOpen, setIsEntriesOpen] = useState(false)

  const [isLaborCostOpen, setIsLaborCostOpen] = useState(false)

  const load = useCallback(async () => {
    const [costResult, entriesResult] = await Promise.all([
      getLaborCost(startDate, endDate),
      getEntries(startDate, endDate),
    ])

    if (costResult) setLaborCost(costResult)

    if (entriesResult) setEntries(entriesResult)
  }, [endDate, getEntries, getLaborCost, startDate])

  const entryCards = useMemo(
    () => entries.map((entry, index) => getTimeClockEntryCard(entry, index, t, i18n.language, true)),
    [entries, t, i18n.language],
  )

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("management.tab_reports")}
              icon={faChartColumn}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={`${defaultStyle.ButtonsActions} flex items-center gap-1`}>
                  <PopupHelpButton
                    variant="global"
                    helpKey="pages.time-clock-reports"
                    title={t("helpPopup:global.timeClockReports.title")}
                    description={t("helpPopup:global.timeClockReports.description")}
                    bullets={t("helpPopup:global.timeClockReports.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGSaveColor}
                    iconColor={THEME.colors.iconBGSecondaryColor}
                    icon={faFilePdf}
                    ariaLabel={t("management.download_pdf")}
                    onClick={() => {
                      if (!employeeId || isLoading) return

                      void downloadReportPdf(employeeId, startDate, endDate)
                    }}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockReportsPopup.button.downloadPdf"
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGPlusColor}
                    iconColor={THEME.colors.iconBGSecondaryColor}
                    icon={faFileCsv}
                    ariaLabel={t("management.download_csv")}
                    onClick={() => {
                      if (isLoading) return

                      void downloadReportCsv(startDate, endDate)
                    }}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockReportsPopup.button.downloadCsv"
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockReportsPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <div className="flex flex-col gap-3 p-2">
              <Animate type={AnimationTypes.slideUpIn} duration="1.6s">
                <div className="flex flex-wrap gap-2">
                  <InputField
                    type="date"
                    value={startDate}
                    placeholder={t("management.start_date")}
                    onChange={(event) => setStartDate(event.target.value)}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockReportsPopup.input.startDate"
                  />

                  <InputField
                    type="date"
                    value={endDate}
                    placeholder={t("management.end_date")}
                    onChange={(event) => setEndDate(event.target.value)}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockReportsPopup.input.endDate"
                  />

                  <GenericDropDown
                    legend={t("management.employee")}
                    initialState={employeeId}
                    data={employees.map((employee) => ({
                      label: employee.employeeName,
                      value: employee.employeeId,
                    }))}
                    handlerFuncion={(event) => setEmployeeId(event.target.value)}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockReportsPopup.dropdown.employee"
                  />
                </div>
              </Animate>

              <div className={defaultStyle.divisionBar}></div>

              <div className={defaultStyle.buttonsController}>
                <Button
                  typeButton="button"
                  label={t("management.period_entries")}
                  size="small"
                  bgColor={THEME.colors.buttonBGPlusColor}
                  iconColor={THEME.colors.iconBGSecondaryColor}
                  onClick={() => setIsEntriesOpen(true)}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockReportsPopup.button.openEntries"
                />

                <Button
                  typeButton="button"
                  label={t("management.labor_cost")}
                  size="small"
                  bgColor={THEME.colors.buttonBGSaveColor}
                  iconColor={THEME.colors.iconBGSecondaryColor}
                  onClick={() => setIsLaborCostOpen(true)}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockReportsPopup.button.openLaborCost"
                />
              </div>
            </div>
          </div>
        </Animate>
      </div>

      {isEntriesOpen && <TimeClockReportsEntriesPopup cards={entryCards} onClose={() => setIsEntriesOpen(false)} />}

      {isLaborCostOpen && <TimeClockLaborCostPopup laborCost={laborCost} onClose={() => setIsLaborCostOpen(false)} />}
    </ModalPortal>
  )
}
